import React, { useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../../../../Context";
import operationData from "../../../../../Operation/data.json";
import { fetchItems, createItem, updateItem, deleteItem } from "../../../../utils/foodDeliveryApi";
import FoodDeliveryTabs from "./FoodDeliveryTabs";

const normalizeListResponse = (response) => {
  if (Array.isArray(response)) return response;
  const candidate = response?.data || response?.items || response?.result || response?.body;
  return Array.isArray(candidate) ? candidate : [];
};

const emptyItemForm = {
  itemName: "",
  price: "",
  category: "",
  description: "",
  image: "",
  veg: false,
  available: true,
  recommended: false,
};

const TAB_META = [
  { id: "all", label: "Total items" },
  { id: "out", label: "Out of stock" },
  { id: "recommended", label: "Recommended" },
];

const MenuManagement = () => {
  const { institutionData } = useContext(Context);
  const institution = operationData.foodDeliveryInstitution;
  const activeInstitution = institution || institutionData?.institution || "";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyItemForm);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubcategories, setExpandedSubcategories] = useState({});

  const loadItems = async () => {
    if (!activeInstitution) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetchItems(activeInstitution);
      setItems(normalizeListResponse(res));
    } catch (err) {
      setError(err?.message || "Failed to load items.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [activeInstitution]);

  const categories = useMemo(
    () => Array.from(new Set((items || []).map((it) => it.category).filter(Boolean))),
    [items]
  );

  const filteredItems = useMemo(() => {
    return (items || []).filter((item) => {
      const isOutOfStock = item.available === false || Number(item.stock) === 0;
      const isRecommended = !!(item.recommended || item.isRecommended || item.featured);
      const searchable = `${item.itemName || ""} ${item.description || ""}`.toLowerCase();

      if (activeTab === "out" && !isOutOfStock) return false;
      if (activeTab === "recommended" && !isRecommended) return false;
      if (searchTerm && !searchable.includes(searchTerm.toLowerCase())) return false;
      return true;
    });
  }, [items, activeTab, searchTerm]);

  const handleCreate = async () => {
    setLoading(true);
    setError("");
    try {
      await createItem(activeInstitution, {
        ...form,
        price: Number(form.price),
      });
      setForm(emptyItemForm);
      await loadItems();
    } catch (err) {
      setError(err?.message || "Failed to create item.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (item) => {
    setLoading(true);
    setError("");
    try {
      const id = item.itemId || item.id;
      await updateItem(activeInstitution, id, {
        ...item,
        price: Number(item.price || 0),
      });
      await loadItems();
    } catch (err) {
      setError(err?.message || "Failed to update item.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (itemId) => {
    setLoading(true);
    setError("");
    try {
      await deleteItem(activeInstitution, itemId);
      await loadItems();
    } catch (err) {
      setError(err?.message || "Failed to delete item.");
    } finally {
      setLoading(false);
    }
  };

  const itemImage = (item) => item.image || item.imageUrl || item.poster || "";

  const getPrice = (item) => {
    let value = item?.price;

    // Mobile app format: price is an array of variants like [{ size, price }]
    if (Array.isArray(value)) {
      if (value.length === 0) return "0.00";
      const first = value[0];
      value = typeof first === "object" && first !== null ? first.price : first;
    }

    // Fallbacks for non-variant payloads
    if (value === undefined || value === null || value === "" || value === 0) {
      value = item?.itemPrice ?? item?.cost ?? item?.amount ?? item?.sellingPrice ?? 0;
    }

    const parsed = parseFloat(value);
    return isNaN(parsed) ? "0.00" : parsed.toFixed(2);
  };

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <FoodDeliveryTabs />
      <div className="px-4 py-6 lg:px-8">
        <div className="mx-auto max-w-5xl space-y-4">
          {/* Header */}
          <div className="rounded-lg border border-gray-200 bg-white px-6 py-6 shadow-sm">
            <h2 className="text-2xl font-extrabold text-[#1F2937]">Food Delivery — Menu Management</h2>
            <p className="mt-1 text-sm text-gray-500">{activeInstitution}</p>
            {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
          </div>

          {/* Search & Filter Bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 mb-3">
              <span className="text-gray-400">🔍</span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search items..."
                className="flex-1 border-0 outline-none text-sm bg-transparent"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} className="text-gray-400 hover:text-gray-600">
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid gap-3 sm:grid-cols-3">
            {TAB_META.map((tab) => {
              const count = (items || []).filter((item) => {
                const isOutOfStock = item.available === false || Number(item.stock) === 0;
                const isRecommended = !!(item.recommended || item.isRecommended || item.featured);
                if (tab.id === "out") return isOutOfStock;
                if (tab.id === "recommended") return isRecommended;
                return true;
              }).length;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`rounded-lg px-4 py-3 text-center font-semibold transition ${
                    isActive
                      ? "bg-gray-900 text-white shadow-md"
                      : "bg-white border border-gray-200 text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-xs mt-1 opacity-75">{tab.label}</div>
                </button>
              );
            })}
          </div>

          {/* Items List - Grouped by Category */}
          <div className="space-y-4">
            {loading && <div className="text-sm text-gray-500 py-4">Loading items...</div>}

            {!loading && filteredItems.length === 0 && (
              <div className="rounded-lg border border-dashed border-gray-300 bg-white px-4 py-8 text-center">
                <span className="text-3xl">🍽️</span>
                <p className="mt-2 text-sm text-gray-600">No Items Yet</p>
                <p className="text-xs text-gray-500">Get started by adding your first menu item.</p>
              </div>
            )}

            {!loading &&
              Array.from(new Set(filteredItems.map((it) => it.category).filter(Boolean))).map((category) => {
                const categoryItems = filteredItems.filter((item) => item.category === category);
                const isExpanded = expandedCategories[category] !== false;
                
                // Group items by subcategory
                const groupedBySubcategory = categoryItems.reduce((acc, item) => {
                  const subcategory = item.subcategory || 'Other';
                  if (!acc[subcategory]) acc[subcategory] = [];
                  acc[subcategory].push(item);
                  return acc;
                }, {});

                const subcategories = Object.keys(groupedBySubcategory);
                
                return (
                  <div key={category} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    {/* Category Header - Collapsible */}
                    <button
                      onClick={() => setExpandedCategories({ ...expandedCategories, [category]: !isExpanded })}
                      className="w-full bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200 hover:from-gray-100 hover:to-gray-150 transition flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`text-lg transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                        <p className="font-bold text-gray-900">{category}</p>
                        <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-gray-200 text-gray-700">{categoryItems.length} items</span>
                      </div>
                    </button>

                    {/* Subcategories */}
                    {isExpanded && (
                      <div>
                        {subcategories.map((subcategory) => {
                          const subcatKey = `${category}-${subcategory}`;
                          const isSubcatExpanded = expandedSubcategories[subcatKey] !== false;
                          const subcatItems = groupedBySubcategory[subcategory];

                          return (
                            <div key={subcatKey}>
                              {/* Subcategory Header - Collapsible */}
                              <button
                                onClick={() => setExpandedSubcategories({ ...expandedSubcategories, [subcatKey]: !isSubcatExpanded })}
                                className="w-full bg-gray-50 px-6 py-2.5 border-b border-gray-100 hover:bg-gray-100 transition flex items-center justify-between text-left"
                              >
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm transition-transform ${isSubcatExpanded ? 'rotate-90' : ''}`}>▶</span>
                                  <p className="font-semibold text-gray-700 text-sm">{subcategory}</p>
                                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-300 text-gray-600">{subcatItems.length}</span>
                                </div>
                              </button>

                              {/* Items in Subcategory */}
                              {isSubcatExpanded && (
                                <div className="divide-y divide-gray-100">
                                  {subcatItems.map((item) => {
                                    const id = item.itemId || item.id || item.itemName;
                                    const imageSrc = itemImage(item);
                                    const isOutOfStock = item.available === false || Number(item.stock) === 0;
                                    const isRecommended = !!(item.recommended || item.isRecommended || item.featured);

                                    return (
                                      <div key={id} className="p-4 hover:bg-blue-50 transition">
                                        <div className="flex gap-4 items-start">
                                          {/* Left: Image */}
                                          <div className="h-20 w-20 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden border border-gray-200">
                                            {imageSrc ? (
                                              <img src={imageSrc} alt={item.itemName} className="h-full w-full object-cover" />
                                            ) : (
                                              <div className="h-full w-full flex items-center justify-center text-gray-400 text-xs font-semibold">No img</div>
                                            )}
                                          </div>

                                          {/* Center: Item Details */}
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-start gap-2">
                                              {/* Veg/Non-veg Indicator - Square with circle */}
                                              <div className={`flex-shrink-0 h-6 w-6 rounded-sm border-2 flex items-center justify-center mt-0.5 ${item.veg ? "border-green-600" : "border-red-600"}`}>
                                                <div className={`h-3 w-3 rounded-full ${item.veg ? "bg-green-600" : "bg-red-600"}`}></div>
                                              </div>
                                              <div className="min-w-0">
                                                <h4 className="font-bold text-gray-900 text-sm">{item.itemName}</h4>
                                                <p className="text-sm font-semibold text-gray-700 mt-0.5">₹{getPrice(item)}</p>
                                                {item.description && (
                                                  <p className="text-xs text-gray-500 line-clamp-2 mt-1">{item.description}</p>
                                                )}
                                                {isOutOfStock && (
                                                  <span className="inline-block mt-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded">Out of stock</span>
                                                )}
                                                {isRecommended && (
                                                  <span className="inline-block mt-1 ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">⭐ Recommended</span>
                                                )}
                                              </div>
                                            </div>
                                          </div>

                                          {/* Right: Action Buttons */}
                                          <div className="flex gap-2 flex-shrink-0">
                                            {/* Recommend Toggle */}
                                            <button
                                              onClick={() => handleSave({ ...item, recommended: !isRecommended })}
                                              className={`px-4 py-2 rounded-lg text-xs font-semibold transition whitespace-nowrap ${
                                                isRecommended
                                                  ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                              }`}
                                              title={isRecommended ? "Remove from recommended" : "Add to recommended"}
                                            >
                                              👍 {isRecommended ? "Recommended" : "Recommend"}
                                            </button>

                                            {/* Stock Toggle */}
                                            <button
                                              onClick={() => handleSave({ ...item, available: !isOutOfStock })}
                                              className={`px-4 py-2 rounded-lg text-xs font-semibold transition whitespace-nowrap ${
                                                isOutOfStock
                                                  ? "bg-red-100 text-red-700 hover:bg-red-200"
                                                  : "bg-green-100 text-green-700 hover:bg-green-200"
                                              }`}
                                              title={isOutOfStock ? "Mark as in stock" : "Mark as out of stock"}
                                            >
                                              {isOutOfStock ? "❌ Out of Stock" : "✓ In Stock"}
                                            </button>

                                            {/* Edit Button */}
                                            <button
                                              onClick={() => {
                                                setSelectedItem(item);
                                                setShowEditModal(true);
                                              }}
                                              className="px-3 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition"
                                              title="Edit item"
                                            >
                                              ✏️ Edit
                                            </button>

                                            {/* Delete Button */}
                                            <button
                                              onClick={() => {
                                                if (window.confirm("Delete this item?")) {
                                                  handleDelete(item.itemId || item.id);
                                                }
                                              }}
                                              className="px-3 py-2 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition"
                                              title="Delete item"
                                            >
                                              🗑️ Delete
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          {/* FAB - Add Item Button */}
          <button
            onClick={() => {
              setSelectedItem(null);
              setForm(emptyItemForm);
              setShowCreateModal(true);
            }}
            className="fixed bottom-8 right-8 h-14 w-14 rounded-full bg-gray-900 text-white shadow-lg hover:bg-gray-800 flex items-center justify-center text-2xl transition hover:scale-110"
            title="Add new item"
          >
            +
          </button>

          {/* Create/Edit Modal */}
          {(showCreateModal || showEditModal) && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
              <div className="w-full max-w-md max-h-96 overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
                <h3 className="text-lg font-bold text-gray-900">{showEditModal ? "Edit Item" : "Add New Item"}</h3>
                <div className="mt-4 space-y-3">
                  <input
                    placeholder="Item name"
                    value={form.itemName}
                    onChange={(e) => setForm((prev) => ({ ...prev, itemName: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    placeholder="Price"
                    type="number"
                    value={form.price}
                    onChange={(e) => setForm((prev) => ({ ...prev, price: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    placeholder="Category"
                    value={form.category}
                    onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                  <input
                    placeholder="Image URL"
                    value={form.image}
                    onChange={(e) => setForm((prev) => ({ ...prev, image: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                  <textarea
                    placeholder="Description"
                    value={form.description}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    rows={2}
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.veg}
                      onChange={(e) => setForm((prev) => ({ ...prev, veg: e.target.checked }))}
                    />
                    <span>Vegetarian</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.available}
                      onChange={(e) => setForm((prev) => ({ ...prev, available: e.target.checked }))}
                    />
                    <span>Available</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.recommended}
                      onChange={(e) => setForm((prev) => ({ ...prev, recommended: e.target.checked }))}
                    />
                    <span>Mark as Recommended</span>
                  </label>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setShowEditModal(false);
                      setForm(emptyItemForm);
                    }}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (showEditModal && selectedItem) {
                        handleSave({ ...selectedItem, ...form });
                      } else {
                        handleCreate();
                      }
                      setShowCreateModal(false);
                      setShowEditModal(false);
                      setForm(emptyItemForm);
                    }}
                    className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                  >
                    {showEditModal ? "Save" : "Create"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuManagement;
